import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { FastField, FastFieldProps } from 'formik';

import { Button, ColorPicker, FormGroup, FormUpload, Input, RotateLoader, Switch } from '@lib/components';
import { CoreUtils } from '@lib/common';
import { UI } from '../../../../../../../core/ui';
import { MobxComponent } from '../../../../../../../mobx/components';
import { RestaurantForm } from '../../../../../../../mobx/components/restaurant-form';
import { openDeferredWindow } from '../../../../../../utils/open_deferred_window';
import {
  InfoBox,
  IntervalToggle,
  PlanSummary,
  nativeSubscriptionActive,
  planName,
  planPeriodText,
  readCheckoutParams,
  stripCheckoutParams,
} from './native-loyalty-billing-ui';

interface Props {
  close?: () => void;
}

interface State {
  connecting: boolean;
  error: string;
  merchantName: string;
  linkEnabled: boolean | null;
  openingAdmin: boolean;
  openingPortal: boolean;
  billingLoaded: boolean;
  unlocked: boolean;
  coveredBy: 'store' | 'organisation' | null;
  /** Whichever plan currently unlocks this store (store or organisation). */
  subscription: T.Schema.NativeLoyaltyBilling.Subscription | null;
  interval: T.Schema.NativeLoyaltyBilling.Interval;
  subscribing: boolean;
  // 'organisation' (default) — this restaurant's loyalty programme is still merged with the
  // rest of the organisation; 'restaurant' — split out. Read-only here (see the "Loyalty Scope"
  // row below); set it from native-loyalty via split/merge ops, not from this dashboard.
  loyaltyScope: 'organisation' | 'restaurant' | null;
}

type FormValues = T.Schema.Restaurant.RestaurantSchema['settings']['loyalty_providers'];

@inject('store')
@observer
export class SettingsFormIntegrationNativeLoyalty extends MobxComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      connecting: false,
      error: '',
      merchantName: '',
      linkEnabled: null,
      openingAdmin: false,
      openingPortal: false,
      billingLoaded: false,
      unlocked: false,
      coveredBy: null,
      subscription: null,
      interval: 'month',
      subscribing: false,
      loyaltyScope: null,
    };
  }

  componentDidMount() {
    const native = this.injected.store.restaurant?.settings.loyalty_providers?.native;
    if (native?.merchant_id) {
      this.refreshLinkStatus();
    }
    this.loadBillingStatus();
  }

  loadBillingStatus = async () => {
    const { store } = this.injected;
    const restaurant = store.restaurant!;
    try {
      const { checkout, sessionId } = readCheckoutParams();
      if (checkout === 'success' && sessionId) {
        const confirmed = await store.api.native_loyalty_billing_confirm({ restaurant_id: restaurant._id, session_id: sessionId });
        if (confirmed.outcome) {
          this.setState({ error: confirmed.message || 'Subscription confirmation failed' });
        } else {
          UI.notification.success('Subscribed to Quaddo');
        }
        stripCheckoutParams();
      } else if (checkout === 'cancel') {
        this.setState({ error: 'Checkout cancelled' });
        stripCheckoutParams();
      }

      const response = await store.api.native_loyalty_billing_status({ restaurant_id: restaurant._id });
      if (response.outcome) {
        this.setState({ billingLoaded: true, error: response.message || 'Failed to load subscription status' });
        return;
      }
      this.setState({
        billingLoaded: true,
        unlocked: response.data.unlocked,
        coveredBy: response.data.covered_by,
        subscription: response.data.subscription,
      });
    } catch (e) {
      this.setState({ billingLoaded: true, error: 'Failed to load subscription status' });
    }
  };

  refreshLinkStatus = async () => {
    const { store } = this.injected;
    const restaurant = store.restaurant!;
    try {
      const response = await store.api.native_loyalty_platform_link({ restaurant_id: restaurant._id });
      if (response.outcome) {
        this.setState({ linkEnabled: nativeEnabledFallback(restaurant) });
        return;
      }
      this.setState({
        linkEnabled: !!response.data.enabled,
        loyaltyScope: response.data.loyaltyScope ?? 'organisation',
      });
    } catch (e) {
      this.setState({ linkEnabled: nativeEnabledFallback(restaurant) });
    }
  };

  connect = async () => {
    const merchantName = this.state.merchantName.trim();
    if (!merchantName) {
      this.setState({ error: 'Merchant name is required' });
      return;
    }

    this.setState({ connecting: true, error: '' });
    const { store } = this.injected;
    const restaurant = store.restaurant!;

    try {
      const response = await store.api.native_loyalty_connect({
        restaurant_id: restaurant._id,
        merchant_name: merchantName,
      });

      if (response.outcome) {
        this.setState({ connecting: false, error: response.message || 'Connect failed' });
        return;
      }

      restaurant.settings.loyalty_providers = restaurant.settings.loyalty_providers || {};
      restaurant.settings.loyalty_providers.native = {
        enabled: true,
        merchant_id: response.data.merchant_id,
        location_id: response.data.location_id,
        platform_link_id: response.data.platform_link_id,
      };
      store.setRestaurant(restaurant);
      this.setState({ connecting: false, linkEnabled: true });
      UI.notification.success('Connected to Quaddo');
    } catch (e) {
      this.setState({ connecting: false, error: 'An error occurred' });
    }
  };

  /** Mints a fresh one-time handoff link every click (see `mintHandoffToken` server-side doc
   * comment) rather than reusing a previous one — the token is single-use and short-TTL, so
   * caching it here would just mean the second click in a row fails. */
  openLoyaltyAdmin = async () => {
    const { store } = this.injected;
    const restaurant = store.restaurant!;
    this.setState({ openingAdmin: true, error: '' });
    // Open the tab now, synchronously with the click, and navigate it once the handoff token
    // comes back — Safari silently blocks window.open() called after an await.
    const navigateAdminWindow = openDeferredWindow();
    try {
      const response = await store.api.native_loyalty_handoff_token({ restaurant_id: restaurant._id });
      if (response.outcome) {
        this.setState({ openingAdmin: false, error: response.message || 'Failed to open Loyalty admin' });
        return;
      }
      navigateAdminWindow(response.data.url);
      this.setState({ openingAdmin: false });
    } catch (e) {
      this.setState({ openingAdmin: false, error: 'An error occurred' });
    }
  };

  subscribe = async () => {
    const { store } = this.injected;
    const restaurant = store.restaurant!;
    this.setState({ subscribing: true, error: '' });
    try {
      const response = await store.api.native_loyalty_billing_checkout({
        restaurant_id: restaurant._id,
        level: 'store',
        interval: this.state.interval,
        return_origin: window.location.origin,
      });
      if (response.outcome) {
        this.setState({ subscribing: false, error: response.message || 'Subscription checkout failed' });
        return;
      }
      window.location.href = response.data.url;
    } catch (e) {
      this.setState({ subscribing: false, error: 'Subscription checkout failed' });
    }
  };

  manageSubscription = async () => {
    const { store } = this.injected;
    const restaurant = store.restaurant!;
    this.setState({ openingPortal: true, error: '' });
    try {
      const response = await store.api.native_loyalty_billing_portal({
        restaurant_id: restaurant._id,
        return_origin: window.location.origin,
      });
      if (response.outcome) {
        this.setState({ openingPortal: false, error: response.message || 'Failed to open subscription portal' });
        return;
      }
      window.location.href = response.data.url;
    } catch (e) {
      this.setState({ openingPortal: false, error: 'Failed to open subscription portal' });
    }
  };

  renderSubscribeView(error: string) {
    const { interval, subscribing } = this.state;
    return (
      <div className="p-4">
        <FormGroup
          title="Subscription"
          help="A Per Store plan is required before this restaurant can connect to Quaddo. Billed through Stripe; cancel any time from the subscription portal."
        >
          <IntervalToggle value={interval} onChange={v => this.setState({ interval: v })} disabled={subscribing} />
          <Button full={true} color="primary" type="button" onClick={this.subscribe} disabled={subscribing}>
            {subscribing && <RotateLoader size={2} color="white" />}
            {!subscribing && 'Subscribe'}
          </Button>
        </FormGroup>
        <InfoBox>
          Running several restaurants? Subscribe once for the whole organisation under{' '}
          <strong className="font-bold">Organization</strong> in the main dashboard.
        </InfoBox>
        {error && <FormGroup error={error} />}
      </div>
    );
  }

  renderSubscriptionRow() {
    const { subscription, coveredBy, openingPortal } = this.state;
    if (!subscription || !coveredBy) {
      return (
        <FormGroup title="Subscription" help="No active Quaddo plan. Loyalty stays off until a plan is active.">
          <p>Not subscribed</p>
        </FormGroup>
      );
    }
    return (
      <FormGroup title="Subscription" help="The plan that currently unlocks Quaddo for this restaurant.">
        <PlanSummary>
          <span className="plan-name">{planName(subscription)}</span>
          <span className="plan-meta">{planPeriodText(subscription)}</span>
        </PlanSummary>
        {coveredBy === 'store' ? (
          <Button color="primary-inverse" full={true} type="button" onClick={this.manageSubscription} disabled={openingPortal}>
            {openingPortal && <RotateLoader size={2} />}
            {!openingPortal && 'Manage subscription'}
          </Button>
        ) : (
          <p className="small">Managed under Organization in the main dashboard.</p>
        )}
      </FormGroup>
    );
  }

  renderStripeRequiredView(error: string) {
    return (
      <div className="p-4">
        <FormGroup
          title="Stripe payments required"
          help="Payments upgrade required. Enable Stripe and complete the Stripe Connect setup before subscribing to Quaddo."
        >
          <p>Contact support to organise your payments upgrade.</p>
        </FormGroup>
        {error && <FormGroup error={error} />}
      </div>
    );
  }

  render() {
    const { store } = this.injected;
    const restaurant = store.restaurant!;
    const native = restaurant.settings.loyalty_providers?.native;
    const {
      connecting,
      error,
      merchantName,
      linkEnabled,
      openingAdmin,
      loyaltyScope,
      billingLoaded,
      unlocked,
    } = this.state;
    const stripeReady = nativeLoyaltyStripeReady(restaurant);

    if (!billingLoaded) {
      return (
        <div className="p-4">
          <RotateLoader size={2} />
        </div>
      );
    }

    if (!native?.merchant_id) {
      if (!stripeReady) return this.renderStripeRequiredView(error);
      if (!unlocked) return this.renderSubscribeView(error);
      return (
        <div className="p-4">
          {this.renderSubscriptionRow()}
          <FormGroup
            title="Merchant Name"
            help="Shown as the merchant name in Quaddo. The restaurant name is used as the location name."
          >
            <Input
              type="text"
              value={merchantName}
              onChange={e => this.setState({ merchantName: e.target.value, error: '' })}
              placeholder="e.g. Acme Restaurants"
            />
          </FormGroup>
          <FormGroup
            title="Connect"
            help="Connect this restaurant to Quaddo so customers can earn and view their points balance."
            no_border={true}
          >
            <Button full={true} color="primary" onClick={this.connect} disabled={connecting}>
              {connecting && <RotateLoader size={2} color="white" />}
              {!connecting && 'Connect'}
            </Button>
          </FormGroup>
          {error && <FormGroup error={error} />}
        </div>
      );
    }

    const initialValues: FormValues = restaurant.settings.loyalty_providers || {};

    return (
      <RestaurantForm<FormValues>
        submit={async (r, values) => {
          const providers = values || {};
          const enabled = !!providers.native?.enabled;
          const sync = await store.api.native_loyalty_platform_link_update({
            restaurant_id: r._id,
            enabled,
          });
          if (sync.outcome) {
            throw new Error(sync.message || 'Failed to update Quaddo link');
          }
          r.settings.loyalty_providers = providers;
          if (r.settings.loyalty_providers.native) {
            r.settings.loyalty_providers.native.enabled = enabled;
          }
          this.setState({ linkEnabled: enabled });
          return {
            r,
            update: { $set: { 'settings.loyalty_providers': r.settings.loyalty_providers } },
          };
        }}
        initialValues={initialValues}
        onSuccess={async () => {
          // Mirror the saved logo to the loyalty portal (empty = Quaddo logo there).
          try {
            const res = await store.api.native_loyalty_sync_logo({ restaurant_id: restaurant._id });
            if (res.outcome) UI.notification.error(`Saved, but the loyalty portal logo was not updated: ${res.message}`);
          } catch {
            UI.notification.error('Saved, but the loyalty portal logo was not updated');
          }
          if (this.props.close) this.props.close();
        }}
        onError={() => UI.notification.error('An error occurred')}
        onSuccessMessage="Settings Updated"
      >
        {({ form, error: formError }) => {
          const { isSubmitting, setFieldValue } = form;
          return (
            <div className='p-4'>
              <FormGroup title="Merchant ID" help="This restaurant's Quaddo merchant id.">
                <p>{native.merchant_id}</p>
              </FormGroup>
              <FormGroup title="Location ID" help="This restaurant's Quaddo location id.">
                <p>{native.location_id}</p>
              </FormGroup>
              <FormGroup title="Platform Link ID" help="Quaddo platform link for this location.">
                <p>{native.platform_link_id || '—'}</p>
              </FormGroup>
              <FormGroup title="Link Status" help="Whether this location's Quaddo platform link is enabled.">
                <p>{linkEnabled == null ? 'Unknown' : linkEnabled ? 'Enabled' : 'Disabled'}</p>
              </FormGroup>
              <FormGroup
                title="Loyalty Scope"
                help="Whether this restaurant's loyalty programme is shared with the rest of the organisation or has been split out on its own. Managed from Quaddo, not editable here."
              >
                <p>
                  {loyaltyScope == null
                    ? 'Unknown'
                    : loyaltyScope === 'organisation'
                    ? 'Shared with organisation'
                    : 'Separate'}
                </p>
              </FormGroup>

              {!stripeReady && (
                <FormGroup
                  title="Stripe payments required"
                  help="Payments upgrade required. Enable Stripe and complete the Stripe Connect setup before enabling Quaddo."
                >
                  <p>Contact support to organise your payments upgrade.</p>
                </FormGroup>
              )}

              {this.renderSubscriptionRow()}

              <FastField name="native.enabled">
                {({ field }: FastFieldProps) => (
                  <FormGroup title="Enabled" help="Let customers earn and view Quaddo points.">
                    <Switch
                      id="native-loyalty-enabled-switch"
                      checked={field.value || false}
                      inputProps={{ disabled: !unlocked || !stripeReady }}
                      onChange={e => setFieldValue('native.enabled', e.target.checked)}
                    />
                  </FormGroup>
                )}
              </FastField>

              <FormGroup
                title="Display Settings"
                help="Branding applied to the loyalty card shown to customers. The logo is also shown on this store's loyalty portal pages."
              />

              <FastField name="native.display.primary_color">
                {({ field }: FastFieldProps) => (
                  <FormGroup
                    title="Card Background Colour"
                    help="Background colour of the loyalty card shown to signed-in customers. Default: #262159."
                    optional={true}
                  >
                    <div className="flex-line centered">
                      <ColorPicker
                        color={field.value || '#262159'}
                        onChange={(color: string) => setFieldValue('native.display.primary_color', color)}
                      />
                      {field.value && (
                        <Button
                          type="button"
                          className="m-l-3"
                          size="xxs"
                          onClick={() => setFieldValue('native.display.primary_color', '')}
                        >
                          Reset
                        </Button>
                      )}
                    </div>
                  </FormGroup>
                )}
              </FastField>

              <FastField name="native.display.secondary_color">
                {({ field }: FastFieldProps) => (
                  <FormGroup
                    title="Accent / QR Colour"
                    help="Used for QR code modules and accent elements on the loyalty card. Default: #c19f62."
                    optional={true}
                  >
                    <div className="flex-line centered">
                      <ColorPicker
                        color={field.value || '#c19f62'}
                        onChange={(color: string) => setFieldValue('native.display.secondary_color', color)}
                      />
                      {field.value && (
                        <Button
                          type="button"
                          className="m-l-3"
                          size="xxs"
                          onClick={() => setFieldValue('native.display.secondary_color', '')}
                        >
                          Reset
                        </Button>
                      )}
                    </div>
                  </FormGroup>
                )}
              </FastField>

              <FastField name="native.display.logo_url">
                {({ field }: FastFieldProps) => (
                  <FormGroup
                    title="Loyalty Card Logo"
                    help="Logo shown on the loyalty card and on this store's loyalty portal pages. Upload an image or paste an https:// URL. If not set, the loyalty card uses the restaurant logo and the portal uses the Quaddo logo."
                    optional={true}
                  >
                    <FormUpload
                      maxSize={300}
                      imagesOnly={true}
                      customSource={true}
                      value={[CoreUtils.image.ucReverse((field.value || '').trim()) || null]}
                      onRemove={() => setFieldValue('native.display.logo_url', '')}
                      onChange={(files: any[]) =>
                        setFieldValue('native.display.logo_url', CoreUtils.image.uc(files[0]) || '')
                      }
                    />
                  </FormGroup>
                )}
              </FastField>

              {formError && <FormGroup error={formError} />}

              <Button
                full={true}
                color="primary"
                type="button"
                className="m-b-2"
                onClick={() => store.router.push(`/restaurant/${restaurant._id}/loyalty/settings`)}
              >
                Loyalty Settings
              </Button>

              <Button
                full={true}
                color="white"
                type="button"
                className="m-b-2"
                onClick={this.openLoyaltyAdmin}
                disabled={openingAdmin}
              >
                {openingAdmin && <RotateLoader size={2} color="white" />}
                {!openingAdmin && 'Open Loyalty Admin'}
              </Button>

              <Button full={true} color="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting && <RotateLoader size={2} color="white" />}
                {!isSubmitting && 'Save'}
              </Button>
            </div>
          );
        }}
      </RestaurantForm>
    );
  }
}

function nativeEnabledFallback(restaurant: T.Schema.Restaurant.RestaurantSchema) {
  return !!restaurant.settings.loyalty_providers?.native?.enabled;
}

export function nativeLoyaltyStripeReady(restaurant: T.Schema.Restaurant.RestaurantSchema) {
  const stripe = restaurant.settings.payments?.stripe;
  return stripe?.enabled === true && typeof stripe.connect_account_id === 'string' && stripe.connect_account_id.length > 0;
}
